import moment from "moment"
import { useCallback, useMemo, useState } from "react"
import toast from "react-hot-toast"
import { DUMMY_MOVEMENT_RAW } from "../data/dummyInventoryMovement"
import type {
  MovementControlRow,
  MovementExportOutputType,
  MovementFilter,
  MovementSortKey,
  MovementSortState
} from "../types/inventoryMovement"
import { DEFAULT_MOVEMENT_FILTER } from "../types/inventoryMovement"
import {
  exportMovementXlsx,
  printMovementHtml
} from "../utils/inventoryMovementExport.common"
import { exportMovementPdf } from "../utils/inventoryMovementPdf"
import {
  buildControlRows,
  computeMovementKpi,
  filterMovementRows,
  sortMovementRows
} from "../utils/inventoryMovementHelpers"

const PROCESSED_ALL = buildControlRows(DUMMY_MOVEMENT_RAW)

export function useInventoryMovementReport() {
  const [filter, setFilter] = useState<MovementFilter>(DEFAULT_MOVEMENT_FILTER)
  const [sort, setSort] = useState<MovementSortState>({
    key: "date",
    asc: true
  })

  const [exportModalOpen, setExportModalOpen] = useState(false)
  const [pendingExportType, setPendingExportType] =
    useState<MovementExportOutputType | null>(null)
  const [printBlocked, setPrintBlocked] = useState(false)

  const [auditRow, setAuditRow] = useState<MovementControlRow | null>(null)

  const periodLabel = useMemo(() => {
    const a = moment(filter.dateFrom)
    const b = moment(filter.dateTo)
    if (!a.isValid() || !b.isValid()) return ""
    if (a.isSame(b, "month") && a.isSame(b, "year"))
      return a.format("MMMM YYYY")
    return `${a.format("MMM D, YYYY")} – ${b.format("MMM D, YYYY")}`
  }, [filter.dateFrom, filter.dateTo])

  const filteredRows = useMemo(
    () => filterMovementRows(PROCESSED_ALL, filter),
    [filter]
  )

  const displayRows = useMemo(
    () => sortMovementRows(filteredRows, sort.key, sort.asc),
    [filteredRows, sort.key, sort.asc]
  )

  const kpi = useMemo(() => computeMovementKpi(displayRows), [displayRows])

  const toggleSort = useCallback((key: MovementSortKey) => {
    setSort((prev) =>
      prev.key === key ? { key, asc: !prev.asc } : { key, asc: true }
    )
  }, [])

  const resetFilter = useCallback(() => {
    setFilter(DEFAULT_MOVEMENT_FILTER)
    setSort({ key: "date", asc: true })
    toast.success("Filter reset")
  }, [])

  const searchReport = useCallback(() => {
    toast.success("Report updated", { duration: 1500 })
  }, [])

  const openExportModal = useCallback((type: MovementExportOutputType) => {
    setPendingExportType(type)
    setPrintBlocked(false)
    setExportModalOpen(true)
  }, [])

  const closeExportModal = useCallback(() => {
    setExportModalOpen(false)
    setPendingExportType(null)
    setPrintBlocked(false)
  }, [])

  const executeExport = useCallback(
    (orientation: "portrait" | "landscape") => {
      const type = pendingExportType
      if (!type) return

      try {
        if (type === "xls") {
          exportMovementXlsx({ periodLabel, rows: displayRows })
          toast.success("Excel exported")
          closeExportModal()
          return
        }
        if (type === "pdf") {
          void exportMovementPdf({
            periodLabel,
            rows: displayRows,
            orientation
          })
            .then(() => {
              toast.success("PDF exported")
              closeExportModal()
            })
            .catch((e: unknown) => {
              console.error(e)
              toast.error("Export failed")
            })
          return
        }
        if (type === "print") {
          const w = printMovementHtml({
            periodLabel,
            rows: displayRows,
            orientation
          })
          if (!w) {
            setPrintBlocked(true)
            return
          }
          closeExportModal()
        }
      } catch (e) {
        toast.error("Export failed")
        console.error(e)
      }
    },
    [pendingExportType, periodLabel, displayRows, closeExportModal]
  )

  const openAudit = useCallback((row: MovementControlRow) => {
    setAuditRow(row)
  }, [])

  const closeAudit = useCallback(() => setAuditRow(null), [])

  return {
    filter,
    setFilter,
    sort,
    toggleSort,
    periodLabel,
    displayRows,
    kpi,
    resetFilter,
    searchReport,
    exportModalOpen,
    printBlocked,
    openExportModal,
    closeExportModal,
    executeExport,
    auditRow,
    openAudit,
    closeAudit
  }
}
